/*
  Shoebox Drive Survey
  Copyright 2025 New Missions
*/

import * as React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { PAGES, SUCCESS_MSG, ERROR_MSG } from './const';
import Page from './components/Page';

import './App.css';

const theme = createTheme({
  colorSchemes: {
    light: {
      palette: {
        primary: {
          main: '#333',
          light: '#333',
          dark: '#333',
          contrastText: '#fff'
        },
        secondary: {
          main: '#ffba14',
          light: '#ffba14',
          dark: '#ffba14',
          contrastText: '#fff'
        }
      }
    }
  },
});

export default function App () {
  const [loading, setLoading] = React.useState(false);
  const [inputError, setInputError] = React.useState(false);
  const [submitFormError, setSubmitError] = React.useState(false);
  const [submitFormCompleted, setSubmitCompleted] = React.useState(false);
  const [page, setPage] = React.useState(0);

  const [inputValues, setInputValues] = React.useState(
    PAGES.map(page => page.questions.map(() => ''))
  );

  function getInputValue (pageIndex: number, questionIndex: number) {
    return inputValues[pageIndex][questionIndex];
  }

  function handleInputChange (pageIndex: number, questionIndex: number, newValue: string) {
    const newValues = [...inputValues];
    newValues[pageIndex][questionIndex] = newValue;
    setInputValues(newValues);
  }

  function submitPage () {
    let hasPageError = false;
    PAGES[page].questions.forEach((question: any, qIndex: number) => {
      if (question.required && !inputValues[page][qIndex]?.trim()) {
        hasPageError = true;
      }
    });

    if (hasPageError) {
      setInputError(true);
      return;
    }
    setInputError(false);

    const nextPage = page + 1;

    if (!PAGES[nextPage]) {
      submitForm();
      return;
    }

    setPage(nextPage);
  }

  async function submitForm () {
    setLoading(true);

    try {
      const data: any = [];

      PAGES.forEach((page, pIndex) => {
        page.questions.forEach((question, qIndex) => {
          data.push({
            id: question.id,
            label: question.label,
            value: inputValues[pIndex][qIndex]
          });
        });
      });

      const res = await fetch('./submit.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      });

      const json = await res.json();

      if (json.status !== 'ok') {
        throw new Error(json.error || 'Unknown error');
      }

      setLoading(false);
      setSubmitCompleted(true);
      setSubmitError(false);
    } catch (err) {
      console.error(err);

      setLoading(false);
      setSubmitCompleted(false);
      setSubmitError(true);
    }
  }

  let content;

  if (loading) {
    content = (
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <CircularProgress color="secondary" />
      </Box>
    );

  } else if (submitFormCompleted) {
    content = (
      <Alert severity="success">{SUCCESS_MSG}</Alert>
    );

  } else {
    content = (
      <>
        {submitFormError
          ? <Alert severity="error" sx={{ mb: 2 }}>{ERROR_MSG}</Alert>
          : null
        }
        <Page
          page={PAGES[page]}
          inputError={inputError}
          getInputValue={getInputValue}
          handleInputChange={handleInputChange}
          submitPage={submitPage}
        />
      </>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ width: '100%', maxWidth: 700, p: 2 }}>
          {content}
        </Box>
      </Box>
    </ThemeProvider>
  );
}
